import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Shield, ShieldAlert, ShieldCheck, ShieldX, Trash2, Plus, Phone, Search, Ban, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_CONFIG = {
  high:   { color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20', icon: ShieldX, label: 'High Risk' },
  medium: { color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20', icon: ShieldAlert, label: 'Medium' },
  low:    { color: 'text-blue-400', bg: 'bg-blue-500/10', border: 'border-blue-500/20', icon: Shield, label: 'Low' },
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-muted border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={color} />
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{label}</span>
      </div>
      <p className="text-2xl font-bold text-foreground">{value}</p>
    </div>
  );
}

export default function SpamManagementPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [lists, setLists] = useState({ allowlist: [], denylist: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | blocked | high | medium
  const [search, setSearch] = useState('');
  const [blockInput, setBlockInput] = useState('');
  const [allowInput, setAllowInput] = useState('');
  const [tab, setTab] = useState('records'); // records | lists

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const blocked_only = filter === 'blocked';
      const [recs, st, ls] = await Promise.all([
        axios.get(`${API}/spam/records?blocked_only=${blocked_only}&limit=100`, { headers }),
        axios.get(`${API}/spam/stats`, { headers }),
        axios.get(`${API}/spam/lists`, { headers }),
      ]);
      let filtered = recs.data.records || [];
      if (filter === 'high') filtered = filtered.filter(r => r.risk === 'high');
      else if (filter === 'medium') filtered = filtered.filter(r => r.risk === 'medium');
      setRecords(filtered);
      setStats(st.data);
      setLists(ls.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [token, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = search
    ? records.filter(r => (r.caller_number || '').includes(search))
    : records;

  const handleUnblock = async (num) => {
    try {
      await axios.post(`${API}/spam/unblock`, { caller_number: num }, { headers });
      toast.success(`${num} removed from spam list`);
      fetchData();
    } catch { toast.error('Failed to unblock'); }
  };

  const handleBlock = async () => {
    if (!blockInput.trim()) return;
    try {
      await axios.post(`${API}/spam/block`, { caller_number: blockInput.trim(), reason: 'manual_block' }, { headers });
      toast.success(`${blockInput} blocked`);
      setBlockInput('');
      fetchData();
    } catch { toast.error('Failed to block number'); }
  };

  const handleAddAllow = async () => {
    if (!allowInput.trim()) return;
    try {
      await axios.post(`${API}/spam/allowlist/add`, { number: allowInput.trim() }, { headers });
      toast.success(`${allowInput} added to allowlist`);
      setAllowInput('');
      fetchData();
    } catch { toast.error('Failed to add'); }
  };

  const handleRemoveAllow = async (num) => {
    try {
      await axios.post(`${API}/spam/allowlist/remove`, { number: num }, { headers });
      toast.success('Removed from allowlist');
      fetchData();
    } catch { toast.error('Failed to remove'); }
  };

  const handleRemoveDeny = async (num) => {
    try {
      await axios.post(`${API}/spam/denylist/remove`, { number: num }, { headers });
      toast.success('Removed from denylist');
      fetchData();
    } catch { toast.error('Failed to remove'); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="spam-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
            <ShieldCheck className="text-emerald-400" size={24} /> Spam Protection
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Manage blocked callers, allowlists, and spam detection</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" className="border-border text-foreground" data-testid="refresh-spam">
          <RefreshCw size={14} /> Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="spam-stats">
          <StatCard icon={ShieldAlert} label="Total Flagged" value={stats.total_flagged} color="text-amber-400" />
          <StatCard icon={ShieldX} label="Blocked" value={stats.total_blocked} color="text-red-400" />
          <StatCard icon={AlertTriangle} label="Today" value={stats.flagged_today} color="text-orange-400" />
          <StatCard icon={Shield} label="This Week" value={stats.flagged_this_week} color="text-blue-400" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-xl p-1 w-fit">
        {[['records', 'Spam Calls'], ['lists', 'Allow / Block Lists']].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === key ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid={`tab-${key}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'records' && (
        <>
          {/* Filters + Search + Manual Block */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex gap-1 bg-muted rounded-lg p-1">
              {[['all', 'All'], ['blocked', 'Blocked'], ['high', 'High'], ['medium', 'Medium']].map(([key, label]) => (
                <button key={key} onClick={() => setFilter(key)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${filter === key ? 'bg-emerald-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`filter-${key}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by phone number..."
                className="pl-9 bg-muted border-border text-foreground placeholder:text-muted-foreground h-9"
                data-testid="spam-search" />
            </div>
            <div className="flex gap-2">
              <Input value={blockInput} onChange={e => setBlockInput(e.target.value)}
                placeholder="+1234567890"
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground h-9 w-40 font-mono text-sm"
                data-testid="manual-block-input" />
              <Button onClick={handleBlock} size="sm" className="bg-red-500/80 hover:bg-red-500 text-white h-9" data-testid="manual-block-btn">
                <Ban size={14} /> Block
              </Button>
            </div>
          </div>

          {/* Records Table */}
          <Card className="border-border bg-card">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
                  <Loader2 size={16} className="animate-spin" /> Loading...
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <ShieldCheck size={32} className="mx-auto mb-2 text-emerald-500/50" />
                  <p className="text-sm">No spam records found</p>
                  <p className="text-xs text-muted-foreground mt-1">Your AI assistant is protected from spam calls</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-800/50">
                  {/* Header */}
                  <div className="grid grid-cols-12 gap-2 px-4 py-2.5 text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
                    <div className="col-span-3">Caller</div>
                    <div className="col-span-2">Risk</div>
                    <div className="col-span-1 text-center">Score</div>
                    <div className="col-span-2">Reasons</div>
                    <div className="col-span-1 text-center">Attempts</div>
                    <div className="col-span-1">Last Seen</div>
                    <div className="col-span-2 text-right">Action</div>
                  </div>
                  {filtered.map((r, i) => {
                    const rc = RISK_CONFIG[r.risk] || RISK_CONFIG.low;
                    const Icon = rc.icon;
                    const lastSeen = r.last_seen_at ? new Date(r.last_seen_at).toLocaleDateString() : '—';
                    return (
                      <div key={r.caller_number + i} className="grid grid-cols-12 gap-2 px-4 py-3 items-center hover:bg-muted transition-colors"
                        data-testid={`spam-record-${i}`}>
                        <div className="col-span-3 flex items-center gap-2">
                          <Phone size={13} className="text-muted-foreground shrink-0" />
                          <span className="text-sm text-foreground font-mono truncate">{r.caller_number || 'Anonymous'}</span>
                        </div>
                        <div className="col-span-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${rc.bg} ${rc.color} ${rc.border} border`}>
                            <Icon size={10} /> {rc.label}
                          </span>
                        </div>
                        <div className="col-span-1 text-center">
                          <span className={`text-sm font-bold ${r.spam_score >= 80 ? 'text-red-400' : r.spam_score >= 40 ? 'text-amber-400' : 'text-blue-400'}`}>
                            {r.spam_score}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <div className="flex flex-wrap gap-1">
                            {(r.reasons || []).slice(0, 2).map(reason => (
                              <span key={reason} className="px-1.5 py-0.5 rounded bg-muted text-[9px] text-muted-foreground font-mono">
                                {reason.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="col-span-1 text-center">
                          <span className="text-sm text-foreground">{r.attempt_count || 0}</span>
                        </div>
                        <div className="col-span-1">
                          <span className="text-xs text-muted-foreground">{lastSeen}</span>
                        </div>
                        <div className="col-span-2 flex justify-end gap-1.5">
                          <Button onClick={() => handleUnblock(r.caller_number)} size="sm" variant="outline"
                            className="h-7 px-2 text-[10px] border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                            data-testid={`unblock-${i}`}>
                            <CheckCircle2 size={10} /> Release
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {tab === 'lists' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Allowlist */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <ShieldCheck size={16} className="text-emerald-400" /> Allowlist
              </CardTitle>
              <p className="text-xs text-muted-foreground">Numbers that will never be blocked</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input value={allowInput} onChange={e => setAllowInput(e.target.value)}
                  placeholder="+1234567890"
                  className="bg-muted border-border text-foreground placeholder:text-muted-foreground font-mono text-sm"
                  data-testid="allowlist-input" />
                <Button onClick={handleAddAllow} size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white shrink-0" data-testid="allowlist-add-btn">
                  <Plus size={14} /> Add
                </Button>
              </div>
              {lists.allowlist.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No numbers allowlisted</p>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                  {lists.allowlist.map(num => (
                    <div key={num} className="flex items-center justify-between p-2.5 bg-muted rounded-lg">
                      <span className="text-sm text-foreground font-mono">{num}</span>
                      <button onClick={() => handleRemoveAllow(num)} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid={`remove-allow-${num}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Denylist */}
          <Card className="border-border bg-card">
            <CardHeader>
              <CardTitle className="text-foreground text-base flex items-center gap-2">
                <ShieldX size={16} className="text-red-400" /> Denylist
              </CardTitle>
              <p className="text-xs text-muted-foreground">Numbers that are always blocked</p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input value={blockInput} onChange={e => setBlockInput(e.target.value)}
                  placeholder="+1234567890"
                  className="bg-muted border-border text-foreground placeholder:text-muted-foreground font-mono text-sm"
                  data-testid="denylist-input" />
                <Button onClick={async () => {
                  if (!blockInput.trim()) return;
                  try {
                    await axios.post(`${API}/spam/denylist/add`, { number: blockInput.trim() }, { headers });
                    toast.success('Added to denylist');
                    setBlockInput('');
                    fetchData();
                  } catch { toast.error('Failed'); }
                }} size="sm" className="bg-red-500/80 hover:bg-red-500 text-white shrink-0" data-testid="denylist-add-btn">
                  <Plus size={14} /> Add
                </Button>
              </div>
              {lists.denylist.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No numbers denylisted</p>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                  {lists.denylist.map(num => (
                    <div key={num} className="flex items-center justify-between p-2.5 bg-muted rounded-lg">
                      <span className="text-sm text-foreground font-mono">{num}</span>
                      <button onClick={() => handleRemoveDeny(num)} className="text-muted-foreground hover:text-red-400 transition-colors" data-testid={`remove-deny-${num}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
